'use client';

import * as React from 'react';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { Globe } from 'lucide-react';
import { locales, localeNames, defaultLocale } from '@/i18n';
import { getLocalizedPath } from '@/lib/locale-utils';
import { getCountryFromSlug, getSlugFromCountry } from '@/lib/country-slugs';
import { getPageFromSlug, getSlugFromPage } from '@/lib/page-slugs';
import { getFeatureFromSlug, getSlugFromFeature, parseFeatureDetailUrl } from '@/lib/feature-slugs';
import { getPlanImageByLocaleSlug, getPlanHreflangSlugs } from '@/lib/mlm-plan-hreflang';

export function LanguageSwitcher() {
  const locale = useLocale();
  const router = useRouter();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isNavigating, setIsNavigating] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleLanguageChange = async (newLocale: string) => {
    // Don't do anything if selecting the same locale
    if (newLocale === locale) {
      setIsOpen(false);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    // Close dropdown immediately for better UX
    setIsOpen(false);
    setIsNavigating(true);

    // Get the current pathname from the browser
    const currentPath = window.location.pathname;

    // Extract the pathname without locale prefix
    let pathWithoutLocale = currentPath;
    let currentLocaleForCheck = locale;

    // Split the path into segments
    const segments = currentPath.split('/').filter(Boolean);

    // Check if the first segment is a locale (but not 'en' since it's default and might not be in URL)
    if (segments.length > 0 && locales.includes(segments[0] as any) && segments[0] !== defaultLocale) {
      // Remove the locale segment to get the base path
      currentLocaleForCheck = segments[0];
      const remainingSegments = segments.slice(1);
      pathWithoutLocale = remainingSegments.length > 0
        ? '/' + remainingSegments.join('/')
        : '/';
    } else {
      // No locale prefix or default locale - use current locale from hook
      currentLocaleForCheck = locale;
      // If there's a locale segment but it's the default, still remove it for path extraction
      if (segments.length > 0 && segments[0] === defaultLocale) {
        const remainingSegments = segments.slice(1);
        pathWithoutLocale = remainingSegments.length > 0
          ? '/' + remainingSegments.join('/')
          : '/';
      }
    }

    // Ensure path starts with /
    if (!pathWithoutLocale.startsWith('/')) {
      pathWithoutLocale = '/' + pathWithoutLocale;
    }

    // Check if the current path is a detail page (features, blog, services)
    // Handle both translated and non-translated page paths
    let featureId: string | null = null;
    let featureFoundLocale: string | null = null;
    let featureSlug: string | null = null;

    let blogPostSlug: string | null = null;
    let blogPageId: string | null = null;
    let blogFoundLocale: string | null = null;

    let serviceSlug: string | null = null;
    let servicePageId: string | null = null;
    let serviceFoundLocale: string | null = null;

    // Track if we detected a features/blog/services page
    let foundPageId: string | null = null;
    let foundPageLocale: string | null = null;

    // Split the path into segments
    const pathSegments = pathWithoutLocale.split('/').filter(Boolean);

    if (pathSegments.length >= 2) {
      // Check if the first segment is a translated page path (features, blog, services)
      const firstSegment = pathSegments[0];
      const pageId = getPageFromSlug(firstSegment, currentLocaleForCheck);

      // Also try with all locales to find a match
      foundPageId = pageId;
      foundPageLocale = currentLocaleForCheck;

      if (!foundPageId) {
        for (const loc of locales) {
          const testPageId = getPageFromSlug(firstSegment, loc);
          if (testPageId === 'mlm-software-features' || testPageId === 'blog' || testPageId === 'mlm-software-services') {
            foundPageId = testPageId;
            foundPageLocale = loc;
            break;
          }
        }
      }
      
      // Update foundPageLocale if we found a page ID
      if (foundPageId && foundPageLocale !== currentLocaleForCheck) {
        // Already set above
      }

      // If it's a features page, check for second segment as feature slug
      if (foundPageId === 'mlm-software-features') {
        featureSlug = decodeURIComponent(pathSegments[1]);
        featureFoundLocale = foundPageLocale; // Set this even if featureId not found yet

        // First try with the detected locale
        featureId = getFeatureFromSlug(featureSlug, foundPageLocale);
        if (featureId) {
          // Feature ID found, keep featureFoundLocale as is
        } else {
          // Try with all locales to find a match
          for (const loc of locales) {
            featureId = getFeatureFromSlug(featureSlug, loc);
            if (featureId) {
              featureFoundLocale = loc;
              break;
            }
          }
        }
        
        // If still not found, try using parseFeatureDetailUrl as fallback
        if (!featureId && featureSlug) {
          const parsed = parseFeatureDetailUrl(pathWithoutLocale, currentLocaleForCheck);
          if (parsed && parsed.featureId) {
            featureId = parsed.featureId;
            featureFoundLocale = currentLocaleForCheck;
          }
        }
        
        // If still not found, try keyword-based fallback detection (same as route page)
        if (!featureId && featureSlug) {
          const slugLower = featureSlug.toLowerCase();
          if (slugLower.includes('bitcoin') && (slugLower.includes('integration') || slugLower.includes('intégration') || slugLower === 'bitcoin')) {
            featureId = 'bitcoin-integration';
          } else if (slugLower.includes('sms') && slugLower.includes('integration')) {
            featureId = 'sms-integration';
          } else if (slugLower.includes('pin')) {
            featureId = 'e-pin';
          } else if (slugLower.includes('wallet') || slugLower.includes('portefeuille') || slugLower.includes('billetera')) {
            featureId = 'e-wallet';
          } else if (slugLower.includes('email') || slugLower.includes('e-mail') || slugLower.includes('courrier') || slugLower.includes('correo')) {
            featureId = 'e-mail-system';
          } else if (slugLower.includes('ticket') || slugLower.includes('support') || slugLower.includes('soporte') || slugLower.includes('soutien') || slugLower.includes('supporto') || slugLower.includes('biglietti')) {
            featureId = 'ticket-system';
          } else if (slugLower.includes('voucher') || slugLower.includes('cupon') || slugLower.includes('bon')) {
            featureId = 'e-voucher';
          } else if (slugLower.includes('replicating') || slugLower.includes('replicador') || slugLower.includes('répliquant') || slugLower.includes('replicante') || slugLower.includes('replizierend') || slugLower.includes('website') || slugLower.includes('sitio') || slugLower.includes('site')) {
            featureId = 'replicating-website';
          } else if (slugLower.includes('genealogy') || slugLower.includes('genealogia') || slugLower.includes('généalogie') || slugLower.includes('genealogia') || slugLower.includes('stammbaum') || slugLower.includes('tree') || slugLower.includes('arbol') || slugLower.includes('arbre') || slugLower.includes('albero')) {
            featureId = 'genealogy-tree';
          } else if (slugLower.includes('marketing') && (slugLower.includes('automation') || slugLower.includes('automatisation') || slugLower.includes('automatizacion') || slugLower.includes('automazione') || slugLower.includes('automatisierung'))) {
            featureId = 'marketing-automation';
          } else if (slugLower.includes('payment') && (slugLower.includes('gateway') || slugLower.includes('passerelle') || slugLower.includes('pasarela') || slugLower.includes('zahlungsgateway'))) {
            featureId = 'payment-gateway-integration';
          } else if ((slugLower.includes('e-commerce') || slugLower.includes('ecommerce') || slugLower.includes('comercio-electronico') || slugLower.includes('commerce-electronique') || slugLower.includes('commercio-elettronico')) && (slugLower.includes('integration') || slugLower.includes('integracion') || slugLower.includes('integrazione') || slugLower.includes('integracao'))) {
            featureId = 'integration-with-e-commerce-solutions';
          } else if ((slugLower.includes('crypto') || slugLower.includes('cryptocurrency') || slugLower.includes('criptomoneda') || slugLower.includes('cryptomonnaie') || slugLower.includes('cryptovaluta') || slugLower.includes('criptomoeda') || slugLower.includes('kryptowaehrung')) && (slugLower.includes('mlm') || slugLower.includes('software'))) {
            featureId = 'crypto-currency-mlm-software';
          }
          
          // If feature ID was found via keyword detection, keep the found locale
          if (featureId) {
            featureFoundLocale = foundPageLocale;
          }
        }
        
        // If feature ID still not found but we know it's a features page,
        // set featureFoundLocale so we can redirect to features listing page
        if (!featureId) {
          featureFoundLocale = foundPageLocale;
        }
      }

      // If it's a blog page, check for second segment as blog post slug
      if (foundPageId === 'blog') {
        // Make sure we have a second segment (the blog post slug)
        if (pathSegments.length >= 2 && pathSegments[1]) {
          blogPostSlug = decodeURIComponent(pathSegments[1]);
          blogPageId = 'blog';
          blogFoundLocale = foundPageLocale;
        }
      }

      // If it's a mlm-software-services page, check for second segment as service slug
      if (foundPageId === 'mlm-software-services') {
        serviceSlug = decodeURIComponent(pathSegments[1]);
        servicePageId = 'mlm-software-services';
        serviceFoundLocale = foundPageLocale;
      }
    }

    // Check if the current path is a plan slug, country page slug, or a page slug (legal, privacy-policy, refund-policy, free-mlm-software-demo, etc.)
    // Decode the URL to handle encoded characters (e.g., %C5%93 -> œ). Trim slashes so trailing slash doesn't break country/page lookup.
    const rawSlug = (pathWithoutLocale.startsWith('/') ? pathWithoutLocale.slice(1) : pathWithoutLocale).replace(/^\/+|\/+$/g, '');
    const decodedSlug = decodeURIComponent(rawSlug).trim();

    // First, check if it's a page slug (legal, privacy-policy, refund-policy, free-mlm-software-demo, etc.)
    // This should be checked BEFORE plans/countries since page slugs are more specific
    let page: string | null = null;
    let pageFoundLocale: string | null = null;

    // Only check for single-segment page slugs if not already detected as feature/blog/service detail page
    if (!featureId && !blogPostSlug && !serviceSlug) {
      // First try with the detected locale
      page = getPageFromSlug(decodedSlug, currentLocaleForCheck);
      if (page) {
        pageFoundLocale = currentLocaleForCheck;
      } else {
        // Try with all locales to find a match
        for (const loc of locales) {
          page = getPageFromSlug(decodedSlug, loc);
          if (page) {
            pageFoundLocale = loc;
            break;
          }
        }
      }
    }

    // For single-segment paths: check country first, then treat as plan only if not a country.
    // (Previously we set planSlug whenever !page, so country was never detected and switcher sent users to plans page.)
    let country: string | null = null;
    let foundLocale: string | null = null;

    if (!page && !featureId && !blogPostSlug && !serviceSlug) {
      // First try with the detected locale
      country = getCountryFromSlug(decodedSlug, currentLocaleForCheck);
      if (country) {
        foundLocale = currentLocaleForCheck;
      } else {
        // Try with all locales to find a match
        for (const loc of locales) {
          country = getCountryFromSlug(decodedSlug, loc);
          if (country) {
            foundLocale = loc;
            break;
          }
        }
      }
    }

    // Plan slug: only if not a page and not a country (actual plan match happens when building newPath)
    let planSlug: string | null = null;
    let planFoundLocale: string | null = null;
    if (!page && !country) {
      // Additional check: if this is a known page slug, don't treat it as a plan
      const knownPageSlugs = [
        // About pages
        'about', 'acerca-de-la-compania', 'a-propos-de-la-societe', 'circa-la-societa', 'ueber-das-unternehmen', 'sobre-nos', 'guan-yu-wo-men',
        // Blog pages
        'blog', 'blogs', 'mlm-software-blog',
        // Contact pages
        'contact', 'contacto', 'nous-contacter', 'contatto', 'kontaktiere-uns', 'contato', 'lian-xi-wo-men',
        // Pricing pages
        'pricing', 'precios', 'tarifs', 'prezzi', 'preise', 'precos', 'ding-jia',
        // FAQ pages
        'faq', 'preguntas-frecuentes', 'foire-aux-questions', 'domande-frequenti', 'haeufig-gestellte-fragen', 'perguntas-frequentes', 'chang-jian-wen-ti',
        // Legal pages
        'legal', 'terminos-legales', 'mentions-legales', 'termini-legali', 'rechtliches', 'termos-legais', 'fa-lu-tiao-kuan',
        // Privacy pages
        'privacy', 'politica-de-privacidad', 'politique-de-confidentialite', 'politica-sulla-privacy', 'datenschutzrichtlinie', 'politica-de-privacidade', 'yin-si-zheng-ce',
        // Other common pages
        'testimonials', 'testimonios', 'temoignages', 'testimonianze', 'referenzen', 'depoimentos', 'ke-hu-zheng-yan',
        'changelog', 'registro-de-cambios', 'journal-des-modifications', 'registro-delle-modifiche', 'aenderungsprotokoll', 'registro-de-mudancas', 'geng-gai-ri-zhi',
        'industry-solutions', 'soluciones-industriales', 'solutions-industrielles', 'soluzioni-industriali', 'branchenloesungen', 'solucoes-industriais', 'hang-ye-jie-jue-fang-an',
        'mlm-calculator', 'calculadora-mlm', 'calculateur-mlm', 'calcolatore-mlm', 'mlm-rechner', 'calculadora-mlm', 'mlm-ji-suan-qi',
        'free-mlm-software-demo', 'demostracion-gratuita-del-software-mlm', 'demo-gratuite-du-logiciel-mlm', 'demo-gratuita-del-software-mlm', 'kostenlose-mlm-software-demo',
        // MLM software pages (listing pages – do not treat as plan)
        'mlm-software-features', 'mlm-software-modules', 'mlm-software-services',
        'services', 'servizi-software-mlm', 'servicios-de-software-mlm', 'services-logiciel-mlm', 'mlm-software-dienstleistungen', 'servicos-de-software-mlm',
      ];
      if (!knownPageSlugs.includes(decodedSlug)) {
        planSlug = decodedSlug.trim();
        planFoundLocale = currentLocaleForCheck;
      }
    }

    let newPath: string;

    // Check page slugs FIRST (this should handle blog, contact, pricing, etc.)
    if (page && pageFoundLocale) {
      // This is a page - get the translated slug for the new locale
      const translatedSlug = getSlugFromPage(page, newLocale);
      if (translatedSlug) {
        // Use the translated slug
        newPath = getLocalizedPath(translatedSlug, newLocale);
      } else {
        // Fallback to original path if translation not found
        newPath = getLocalizedPath(pathWithoutLocale, newLocale);
      }
      router.push(newPath);
      setTimeout(() => setIsNavigating(false), 100);
      return; // IMPORTANT: Return here to prevent further processing
    // Check about page slugs specifically (after general pages)
    } else if (decodedSlug && ['about', 'acerca-de-la-compania', 'a-propos-de-la-societe', 'circa-la-societa', 'ueber-das-unternehmen', 'sobre-nos', 'guan-yu-wo-men'].includes(decodedSlug)) {
      console.log('ABOUT PAGE HANDLING:', { decodedSlug, newLocale });
      // This is an about page - get the translated slug for the new locale
      const translatedSlug = getSlugFromPage('about', newLocale);
      if (translatedSlug) {
        // Use the translated slug
        newPath = getLocalizedPath(translatedSlug, newLocale);
      } else {
        // Fallback to original path if translation not found
        newPath = getLocalizedPath(pathWithoutLocale, newLocale);
      }
    } else if (blogPostSlug && blogPageId && blogFoundLocale) {
      console.log('Detected as blog post, routing to blog matching');
      // This is a blog post page - find the matching blog post slug in the target locale
      try {
        const matchRes = await fetch(
          `/api/blog/match?sourceSlug=${encodeURIComponent(blogPostSlug)}&sourceLocale=${blogFoundLocale}&targetLocale=${newLocale}`,
          { cache: 'no-store' }
        );

        let matchData;
        try {
          matchData = await matchRes.json();
        } catch (parseError) {
          // If response is not JSON, treat as error
          console.error('Failed to parse blog match response:', parseError);
          matchData = { matched: false };
        }

        // Check if we got a successful match
        if (matchRes.ok && matchData) {
          if (matchData.matched === true && matchData.slug) {
            // Found matching blog post - use slug only (blog posts at /[locale]/[slug])
            newPath = getLocalizedPath(matchData.slug, newLocale);
          } else {
            // No match found (blog post doesn't exist in target locale) - redirect to blog listing
            const translatedBlogPath = getSlugFromPage('blog', newLocale) || 'blog';
            newPath = getLocalizedPath(translatedBlogPath, newLocale);
          }
        } else {
          // API error (500) - log for debugging
          console.error('Blog post match API error:', {
            status: matchRes.status,
            statusText: matchRes.statusText,
            matchData,
            blogPostSlug,
            blogFoundLocale,
            newLocale
          });
          // On 500 error, still try to navigate to the blog post with same slug
          // This allows the page to handle it gracefully (show 404 if it doesn't exist)
          // Only redirect to listing page if it's a clear 404 from the API
          if (matchRes.status === 404 || (matchData && matchData.matched === false)) {
            const translatedBlogPath = getSlugFromPage('blog', newLocale) || 'blog';
            newPath = getLocalizedPath(translatedBlogPath, newLocale);
          } else {
            // Server error (500) - try same slug anyway (blog posts at /[locale]/[slug])
            newPath = getLocalizedPath(blogPostSlug, newLocale);
          }
        }
      } catch (error) {
        // Log the error for debugging
        console.error('Error fetching blog post match:', error);
        const translatedBlogPath = getSlugFromPage('blog', newLocale) || 'blog';
        newPath = getLocalizedPath(translatedBlogPath, newLocale);
      }
    } else if (serviceSlug && servicePageId && serviceFoundLocale) {
      // This is a service detail page - translate the service page path but keep the same service slug
      const translatedServicePath = getSlugFromPage('mlm-software-services', newLocale) || 'mlm-software-services';
      const fullPath = `${translatedServicePath}/${serviceSlug}`;
      newPath = getLocalizedPath(fullPath, newLocale);
    } else if (page && pageFoundLocale) {
      // This is a page (legal, privacy-policy, refund-policy, free-mlm-software-demo, mlm-calculator, etc.) - get the translated slug for the new locale
      const translatedSlug = getSlugFromPage(page, newLocale);
      if (translatedSlug) {
        // Use the translated slug
        newPath = getLocalizedPath(translatedSlug, newLocale);
      } else {
        // Fallback to original path if translation not found
        newPath = getLocalizedPath(pathWithoutLocale, newLocale);
      }
    } else if (featureFoundLocale && pathSegments.length >= 2 && foundPageId === 'mlm-software-features') {
      // This is a feature detail page (even if featureId not found, we know it's a features page)
      const translatedPagePath = getSlugFromPage('mlm-software-features', newLocale) || 'mlm-software-features';
      
      if (featureId && featureSlug) {
        // We have a valid feature ID - get the translated feature slug
        let translatedFeatureSlug = getSlugFromFeature(featureId, newLocale);

        // If translation not found, try English as fallback
        if (!translatedFeatureSlug && newLocale !== 'en') {
          translatedFeatureSlug = getSlugFromFeature(featureId, 'en');
        }

        if (translatedFeatureSlug) {
          // Use the translated feature slug
          const fullPath = `${translatedPagePath}/${translatedFeatureSlug}`;
          newPath = getLocalizedPath(fullPath, newLocale);
        } else {
          // Feature slug not found in any locale - redirect to features listing page
          newPath = getLocalizedPath(translatedPagePath, newLocale);
        }
      } else {
        // This is the base features page (no feature slug) - use translated page path
        newPath = getLocalizedPath(translatedPagePath, newLocale);
      }
    } else if (country && foundLocale) {
      // Country page - get the translated country slug for the new locale (before plan so country always wins)
      const translatedSlug = getSlugFromCountry(country, newLocale);
      if (translatedSlug) {
        newPath = getLocalizedPath(translatedSlug, newLocale);
      } else {
        newPath = getLocalizedPath(pathWithoutLocale, newLocale);
      }
    } else if (planSlug && planFoundLocale) {
      console.log('PROCESSING AS PLAN - This should not happen for blog page!');
      // Single-segment path: might be a blog post (now at /[locale]/[slug] without blog prefix)
      // Check if this is actually an about page slug that was misidentified as a plan
      const knownAboutSlugs = [
        'about', 'acerca-de-la-compania', 'a-propos-de-la-societe', 'circa-la-societa', 'ueber-das-unternehmen', 'sobre-nos', 'guan-yu-wo-men'
      ];
      if (knownAboutSlugs.includes(planSlug)) {
        console.log('Corrected: This is actually an about page, not a plan');
        // This is actually an about page, not a plan - handle it as about page
        const translatedSlug = getSlugFromPage('about', newLocale);
        if (translatedSlug) {
          newPath = getLocalizedPath(translatedSlug, newLocale);
        } else {
          newPath = getLocalizedPath(pathWithoutLocale, newLocale);
        }
      } else if (pathSegments.length === 1) {
        // Try blog match first so we don't redirect blog posts to the plan page
        try {
          const blogMatchRes = await fetch(
            `/api/blog/match?sourceSlug=${encodeURIComponent(planSlug)}&sourceLocale=${planFoundLocale}&targetLocale=${newLocale}`,
            { cache: 'no-store' }
          );
          let blogMatchData: { matched?: boolean; slug?: string } | null = null;
          try {
            blogMatchData = await blogMatchRes.json();
          } catch {
            // ignore
          }
          if (blogMatchRes.ok && blogMatchData?.matched === true && blogMatchData.slug) {
            newPath = getLocalizedPath(blogMatchData.slug, newLocale);
            router.push(newPath);
            setTimeout(() => setIsNavigating(false), 100);
            return;
          }
        } catch {
          // Not a blog post or API error, fall through to plan handling
        }
      }

      // This is a plan detail page - find the plan slug for the new locale
      // First try known translated slugs (hreflang map) so we don't depend on DB; never send known plans to listing
      const normalizedPlanSlug = (planSlug || '').trim();
      const planImage = getPlanImageByLocaleSlug(planFoundLocale || locale, normalizedPlanSlug);
      const hreflangSlugs = planImage ? getPlanHreflangSlugs(planImage) : null;
      const targetSlugFromMap = hreflangSlugs?.[newLocale];

      if (targetSlugFromMap) {
        newPath = getLocalizedPath(targetSlugFromMap, newLocale);
      } else if (hreflangSlugs && Object.keys(hreflangSlugs).length > 0) {
        // Known plan (we have map) but target locale missing – use English slug for target
        const fallbackSlug = hreflangSlugs['en'] ?? normalizedPlanSlug;
        newPath = getLocalizedPath(fallbackSlug, newLocale);
      } else {
        // Not in hreflang map – use match API (for plans without translated slugs)
        try {
          const matchRes = await fetch(
            `/api/mlm-plans/match?sourceSlug=${encodeURIComponent(normalizedPlanSlug)}&sourceLocale=${planFoundLocale}&targetLocale=${newLocale}`,
            { cache: 'no-store' }
          );

          if (matchRes.ok) {
            const matchData = await matchRes.json();
            if (matchData && matchData.matched && matchData.slug) {
              newPath = getLocalizedPath(matchData.slug, newLocale);
            } else {
              const plansPageSlug = getSlugFromPage('mlm-software-plans', newLocale) || 'mlm-software-plans';
              newPath = getLocalizedPath(plansPageSlug, newLocale);
            }
          } else {
            const plansPageSlug = getSlugFromPage('mlm-software-plans', newLocale) || 'mlm-software-plans';
            newPath = getLocalizedPath(plansPageSlug, newLocale);
          }
        } catch (error) {
          const plansPageSlug = getSlugFromPage('mlm-software-plans', newLocale) || 'mlm-software-plans';
          newPath = getLocalizedPath(plansPageSlug, newLocale);
        }
      }
    } else {
      // Not a feature, plan, country page or page slug - use existing logic
      newPath = getLocalizedPath(pathWithoutLocale, newLocale);
    }

    // Navigate to the locale-specific path using Next.js router (no page reload)
    router.push(newPath);
    
    // Reset navigating state after a short delay
    setTimeout(() => {
      setIsNavigating(false);
    }, 100);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isNavigating}
        aria-label={`Change language. Current language: ${localeNames[locale as keyof typeof localeNames]}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className="flex items-center space-x-2 px-3 py-2 h-[40px] rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Globe className={`h-4 w-4 ${isNavigating ? 'animate-spin' : ''}`} aria-hidden="true" />
        <span className='hidden md:block'>{localeNames[locale as keyof typeof localeNames]}</span>
      </button>
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 z-50"
          role="menu"
          aria-label="Language options"
        >
          <div className="py-1">
            {locales.map((loc) => (
              <button
                key={loc}
                onClick={() => handleLanguageChange(loc)}
                role="menuitem"
                aria-label={`Switch to ${localeNames[loc]}`}
                aria-current={locale === loc ? 'true' : undefined}
                className={`block w-full text-left px-4 py-2 text-sm ${locale === loc
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  } transition-colors`}
              >
                {localeNames[loc]}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
